// One fee component: a percentage of the price, rounded to the cent, plus a flat fee.
//
// This rounds integer cents, half up. ResaleListing#list_price now hands the fee engine a
// BigDecimal, which rounds an exact half cent the same way, so the two agree at every
// price — verified across 50c-$500 at 3/4/6%. Do NOT "fix" this to match a Ruby float:
// that is the rounding that quoted the seller more than fulfill! paid (O-5221).
const feeCents = (priceCents, percentage, flatCents) =>
  Math.round((priceCents * (percentage || 0)) / 100) + (flatCents || 0);

// When the venue absorbs fees, the buyer pays the list price flat and the venue's own
// buyer-side fees come out of it — so they are a deduction from the seller's proceeds
// too, on top of the seller's own listing fee. Passing null means fees are added on top
// of the list price and none of this comes out of the seller (O-5221).
//
// adjust_for_absorbing pays these out of the price one at a time and stops when it runs
// out (#claim_from_remaining), so the total can never exceed the price. Percentages never
// get close, but a $2.00 flat fee on a 50c listing is capped at 50c — without the clamp
// the wizard shows a deduction bigger than the ticket. Only the total is rendered, so
// mirroring that is enough.
//
// The tax is charged on top, so it is not itself a deduction — but processing is taken on
// the taxed total, and under absorb that comes out of the list price. So a 7% tax on a
// $50 listing costs the seller 11c, not $3.50.
const absorbedBuyerFeeCents = (priceCents, absorbedBuyerFees) => {
  if (!absorbedBuyerFees) return 0;
  const {
    opendatePercentage,
    organizerPercentage,
    opendateFlatCents,
    organizerFlatCents,
    processingPercentage,
    processingFlatCents,
    taxRate,
  } = absorbedBuyerFees;

  const taxedPriceCents = priceCents + Math.round((priceCents * (taxRate || 0)) / 100);

  const total =
    feeCents(priceCents, opendatePercentage, opendateFlatCents) +
    feeCents(priceCents, organizerPercentage, organizerFlatCents) +
    feeCents(taxedPriceCents, processingPercentage, processingFlatCents);

  return Math.min(total, priceCents);
};

// Rates for the picked tickets, or null when nothing absorbs. SelectTicketsStep locks the
// selection to one ticket type, as does ResaleListing#all_items_share_ticket_type.
// The tax rate rides along rather than as its own argument — it only matters through the
// absorbed processing fee, so it is dead weight whenever this returns null.
export const absorbedRatesForSelection = (listableTickets, selectedTicketIds, rates) => {
  if (!rates) return null;
  const selected = (listableTickets || []).filter((t) =>
    (selectedTicketIds || []).includes(t.id),
  );
  const absorbing = selected.find((t) => t.ticket_type?.absorb_fees);
  if (!absorbing) return null;

  return {
    ...rates,
    taxRate: absorbing.ticket_type?.tax_rate || 0,
  };
};

export const computeSellerBreakdown = (
  priceCents,
  ticketCount,
  opendatePct,
  organizerPct,
  opendateFlatCents,
  organizerFlatCents,
  absorbedBuyerFees = null,
) => {
  const count = Math.max(ticketCount, 1);
  const totalListingCents = priceCents * count;
  const perTicketFee =
    feeCents(priceCents, opendatePct, opendateFlatCents) +
    feeCents(priceCents, organizerPct, organizerFlatCents);
  const serviceFeeCents = perTicketFee * count;
  const absorbedFeeCents = absorbedBuyerFeeCents(priceCents, absorbedBuyerFees) * count;
  const sellerOwedCents = Math.max(
    totalListingCents - serviceFeeCents - absorbedFeeCents,
    0,
  );
  return { totalListingCents, serviceFeeCents, absorbedFeeCents, sellerOwedCents };
};
